/**
 * Alícuota — porcentaje de participación de una unidad en un condominio.
 *
 * La suma de alícuotas de todas las unidades de un condominio debe
 * totalizar 100% (Ley 21.442). Precisión a 6 decimales para soportar
 * unidades con porcentajes finos (0.000001%).
 *
 * Representación interna: número entre 0 y 1 (no 0-100), escalado en
 * 1_000_000 para aritmética entera donde sea necesario.
 */

export type Alicuota = {
  /** Porcentaje como fracción [0, 1]. Ej: 0.01 = 1%. */
  readonly fraction: number;
};

const PRECISION = 1_000_000;

/** Crea una alícuota desde porcentaje (0-100). Ej: alicuota(5.5) = 5.5%. */
export function alicuota(percentage: number): Alicuota {
  if (!Number.isFinite(percentage)) throw new Error('alicuota must be finite');
  if (percentage < 0 || percentage > 100) {
    throw new Error(`alicuota out of range: ${percentage}`);
  }
  const fraction = Math.round((percentage / 100) * PRECISION) / PRECISION;
  return Object.freeze({ fraction });
}

/** Alícuota desde fracción [0, 1] directa. */
export function alicuotaFromFraction(fraction: number): Alicuota {
  if (!Number.isFinite(fraction)) throw new Error('fraction must be finite');
  if (fraction < 0 || fraction > 1) {
    throw new Error(`fraction out of range: ${fraction}`);
  }
  return Object.freeze({ fraction: Math.round(fraction * PRECISION) / PRECISION });
}

/** Devuelve el porcentaje como número 0-100. */
export function toPercentage(a: Alicuota): number {
  return a.fraction * 100;
}

/** Formato legible: "5,5000%" con coma decimal chilena. */
export function formatAlicuota(a: Alicuota, locale = 'es-CL'): string {
  const pct = toPercentage(a);
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(pct)}%`;
}

/**
 * Valida que una lista de alícuotas sume 100% (con tolerancia de 0.0001%).
 * La tolerancia cubre errores de punto flotante y ajustes menores.
 */
export function sumsToHundred(alicuotas: readonly Alicuota[]): boolean {
  const total = alicuotas.reduce((acc, a) => acc + a.fraction, 0);
  return Math.abs(total - 1) < 0.000_001;
}
